import React, { useEffect, useState, useMemo, useCallback, useRef, useLayoutEffect } from 'react';
import ReactDOM from 'react-dom';
import { X, Info, Wrench, BarChart3, Clock } from 'lucide-react';
import {
  Area,
  AreaChart,
  CartesianGrid,
  ReferenceLine,
  Tooltip,
  XAxis,
  YAxis
} from 'recharts';
import { fetchEquipmentConsult, fetchEquipmentProduction } from '../services/equipmentConsultService';
import EquipmentConsultProductionPeriodPicker, {
  getDefaultProductionPeriod,
} from './EquipmentConsultProductionPeriodPicker';
import OpenOsV2EquipmentHistoryOverlay from '../../new-os/components/OpenOsV2EquipmentHistoryOverlay';
import './EquipmentConsultDetailModal.css';

const formatContador = (val) => {
  if (val == null || val === '') return '—';
  const n = typeof val === 'number' ? val : parseInt(val, 10);
  return Number.isNaN(n) ? String(val) : n.toLocaleString('pt-BR');
};

/** Converte rótulo dd/MM/yyyy do eixo X para timestamp (ordem cronológica). */
const parseTb02117DataBr = (s) => {
  if (s == null || typeof s !== 'string') return NaN;
  const m = String(s).trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return NaN;
  const d = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  const y = parseInt(m[3], 10);
  const t = new Date(y, mo - 1, d).getTime();
  return Number.isNaN(t) ? NaN : t;
};

/** Eixo Y do gráfico: separador de milhar / milhão (pt-BR). */
const CHART_HEIGHT = 148;

const formatChartAxisTick = (val) => {
  if (val == null || val === '') return '';
  const n = typeof val === 'number' ? val : Number(val);
  if (Number.isNaN(n)) return String(val);
  return n.toLocaleString('pt-BR', { maximumFractionDigits: 0 });
};

const TOOLTIP_EST_HALF_W = 92;
const TOOLTIP_EDGE_PAD = 8;

/** Mantém o tooltip dentro do plot e acima/abaixo conforme o ponto. */
function getProductionTooltipLayout(coordinate, viewBox) {
  if (!coordinate || !viewBox) {
    return { x: 0, y: 0, above: true };
  }
  const maxX = viewBox.width - TOOLTIP_EDGE_PAD - TOOLTIP_EST_HALF_W;
  const minX = TOOLTIP_EDGE_PAD + TOOLTIP_EST_HALF_W;
  const x = Math.max(minX, Math.min(maxX, coordinate.x));
  const above = coordinate.y > viewBox.height * 0.42;
  return { x, y: coordinate.y, above };
}

function ProductionChartTooltip({ active, payload, label, coordinate, viewBox }) {
  if (!active || !payload?.length) return null;
  const { above } = getProductionTooltipLayout(coordinate, viewBox);
  return (
    <div
      className="equipment-consult-chart-tooltip"
      style={{
        transform: above ? 'translate(-50%, calc(-100% - 8px))' : 'translate(-50%, 8px)',
      }}
    >
      <div className="equipment-consult-chart-tooltip-date">{label}</div>
      {payload.map((entry) => (
        <div key={entry.dataKey} className="equipment-consult-chart-tooltip-row">
          <span className="equipment-consult-chart-tooltip-dot" style={{ background: entry.color }} />
          <span>
            {entry.name} : {formatContador(entry.value)}
          </span>
        </div>
      ))}
    </div>
  );
}

const hasText = (v) => {
  if (v == null) return false;
  return String(v).trim() !== '';
};

/** Compara data dd/MM/yyyy (valor exibido do SQL) com hoje, só calendário local. */
const isVencContratoAntesDeHoje = (venccBr) => {
  if (!hasText(venccBr)) return false;
  const m = String(venccBr).trim().match(/^(\d{2})\/(\d{2})\/(\d{4})$/);
  if (!m) return false;
  const d = parseInt(m[1], 10);
  const mo = parseInt(m[2], 10);
  const y = parseInt(m[3], 10);
  const v = new Date(y, mo - 1, d);
  const t = new Date();
  const today = new Date(t.getFullYear(), t.getMonth(), t.getDate());
  return v < today;
};

const openGoogleMaps = (endereco) => {
  if (!hasText(endereco)) return;
  const q = encodeURIComponent(String(endereco).trim());
  window.open(`https://www.google.com/maps/search/?api=1&query=${q}`, '_blank', 'noopener,noreferrer');
};

const CONTADOR_FIELDS = [
  { field: 'TB02122_CONTADOR', label: 'A4 P&B' },
  { field: 'TB02122_CONTADORC', label: 'A4 Cor' },
  { field: 'TB02122_CONTADORDG', label: 'Dig.' },
  { field: 'TB02122_CONTADORGF', label: 'A3 P&B' },
  { field: 'TB02122_CONTADORGFC', label: 'A3 Cor' }
];

function EcV2Field({ label, children, valueClassName = '' }) {
  if (children == null || children === '') return null;
  if (typeof children === 'string' && !hasText(children)) return null;
  return (
    <div>
      <span className="equipment-consult-v2-field-label">{label}</span>
      <span className={`equipment-consult-v2-field-value ${valueClassName}`.trim()}>{children}</span>
    </div>
  );
}

function EcHistoricoAtendimentoBlock({ serie, historicoMounted, onToggle }) {
  return (
    <div className="equipment-consult-v2-historico">
      <button
        type="button"
        className={`equipment-consult-v2-historico-btn${historicoMounted ? ' is-active' : ''}`}
        onClick={onToggle}
        aria-expanded={historicoMounted}
        disabled={!serie}
      >
        <Clock size={12} strokeWidth={2} aria-hidden />
        Histórico de serviço
      </button>
    </div>
  );
}

/**
 * @param {{ open: boolean, selection: { serie: string, patrimonio?: string, produto?: string }|null, onClose: () => void }} props
 */
const EquipmentConsultDetailModal = ({ open, selection, onClose }) => {
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);
  const [contract, setContract] = useState(null);
  const [lastService, setLastService] = useState(null);
  const [production, setProduction] = useState([]);
  const [productionPeriod, setProductionPeriod] = useState(() => getDefaultProductionPeriod());
  const [productionLoading, setProductionLoading] = useState(false);
  const [productionError, setProductionError] = useState(null);

  const [historicoOpen, setHistoricoOpen] = useState(false);
  const [historicoMounted, setHistoricoMounted] = useState(false);
  const historicoCloseTimerRef = useRef(null);

  const serie = selection?.serie ? String(selection.serie).trim() : '';

  const closeHistorico = useCallback(() => {
    setHistoricoOpen(false);
    if (historicoCloseTimerRef.current) {
      clearTimeout(historicoCloseTimerRef.current);
    }
    historicoCloseTimerRef.current = setTimeout(() => {
      setHistoricoMounted(false);
      historicoCloseTimerRef.current = null;
    }, 220);
  }, []);

  const openHistorico = useCallback(() => {
    if (historicoCloseTimerRef.current) {
      clearTimeout(historicoCloseTimerRef.current);
      historicoCloseTimerRef.current = null;
    }
    setHistoricoMounted(true);
    requestAnimationFrame(() => setHistoricoOpen(true));
  }, []);

  const toggleHistorico = useCallback(() => {
    if (historicoMounted) closeHistorico();
    else openHistorico();
  }, [historicoMounted, closeHistorico, openHistorico]);

  useEffect(() => {
    if (!open) {
      setHistoricoOpen(false);
      setHistoricoMounted(false);
      if (historicoCloseTimerRef.current) {
        clearTimeout(historicoCloseTimerRef.current);
        historicoCloseTimerRef.current = null;
      }
    }
  }, [open]);

  useEffect(
    () => () => {
      if (historicoCloseTimerRef.current) {
        clearTimeout(historicoCloseTimerRef.current);
      }
    },
    []
  );

  useEffect(() => {
    if (!open || !selection?.serie) {
      setContract(null);
      setLastService(null);
      setProduction([]);
      setProductionPeriod(getDefaultProductionPeriod());
      setProductionError(null);
      setError(null);
      setLoading(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchEquipmentConsult(selection.serie)
      .then((data) => {
        if (cancelled) return;
        setContract(data.contract || null);
        setLastService(data.lastService || null);
        setProduction(Array.isArray(data.production) ? data.production : []);
        setProductionPeriod(getDefaultProductionPeriod());
        setProductionError(null);
      })
      .catch((e) => {
        if (cancelled) return;
        setError(e.message || 'Erro ao carregar');
        setContract(null);
        setLastService(null);
        setProduction([]);
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => {
      cancelled = true;
    };
  }, [open, selection?.serie]);

  const loadProduction = useCallback(
    (period) => {
      if (!selection?.serie || !period?.startDate || !period?.endDate) return;
      setProductionLoading(true);
      setProductionError(null);
      fetchEquipmentProduction(selection.serie, {
        dataInicio: period.startDate,
        dataFim: period.endDate,
      })
        .then((rows) => {
          setProduction(rows);
          setProductionPeriod(period);
        })
        .catch((e) => {
          setProductionError(e.message || 'Erro ao carregar produção');
        })
        .finally(() => setProductionLoading(false));
    },
    [selection?.serie]
  );

  const cicloMensal = useMemo(() => {
    const v = contract?.TB01010_COPIA;
    if (v == null || v === '') return null;
    const n = Math.trunc(Number(v));
    return Number.isNaN(n) || n <= 0 ? null : n;
  }, [contract]);

  const chartData = useMemo(() => {
    if (!production.length) return [];
    const sorted = [...production].sort((a, b) => {
      const ta = parseTb02117DataBr(a.TB02117_DATA);
      const tb = parseTb02117DataBr(b.TB02117_DATA);
      const aOk = !Number.isNaN(ta);
      const bOk = !Number.isNaN(tb);
      if (aOk && bOk) return ta - tb;
      if (aOk) return -1;
      if (bOk) return 1;
      return 0;
    });
    return sorted.map((row) => ({
      data: row.TB02117_DATA || '',
      'P&B': Number(row.TB02117_TOTPB) || 0,
      Cor: Number(row.TB02117_TOTCOLOR) || 0,
      Digitalização: Number(row.TB02117_TOTDG) || 0
    }));
  }, [production]);

  const chartYMax = useMemo(() => {
    let max = cicloMensal || 0;
    chartData.forEach((row) => {
      const total = (row['P&B'] || 0) + (row.Cor || 0) + (row.Digitalização || 0);
      if (total > max) max = total;
    });
    if (max <= 0) return undefined;
    return Math.ceil(max * 1.12);
  }, [chartData, cicloMensal]);

  const chartWrapRef = useRef(null);
  const [chartWidth, setChartWidth] = useState(0);

  useLayoutEffect(() => {
    if (!open || !chartData.length) {
      setChartWidth(0);
      return undefined;
    }
    const el = chartWrapRef.current;
    if (!el) return undefined;
    const update = () => {
      const w = Math.floor(el.clientWidth);
      if (w > 0) setChartWidth((prev) => (prev === w ? prev : w));
    };
    update();
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, [open, chartData.length]);

  if (!open || !selection) return null;

  const headerLine = [serie, selection.patrimonio].filter(Boolean).join(' • ');
  const produtoLine = selection.produto || contract?.TB01010_NOME || '';

  const overlay = (
    <div className="equipment-consult-detail-overlay" role="presentation" onClick={onClose}>
      <div
        className="equipment-consult-detail-shell"
        role="dialog"
        aria-modal="true"
        aria-labelledby="equipment-consult-detail-title"
        onMouseDown={(e) => e.stopPropagation()}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="equipment-consult-v2-modal">
          <header className="equipment-consult-v2-header">
            <div className="equipment-consult-v2-header-main">
              <h2 id="equipment-consult-detail-title" className="equipment-consult-v2-title">
                {headerLine || '—'}
              </h2>
              <div className="equipment-consult-v2-badges">
                {produtoLine ? (
                  <span className="equipment-consult-v2-badge equipment-consult-v2-badge--muted">{produtoLine}</span>
                ) : null}
                {hasText(contract?.TB02112_DATA) ? (
                  <span className="equipment-consult-v2-badge equipment-consult-v2-badge--muted">
                    Instalação: {contract.TB02112_DATA}
                  </span>
                ) : null}
                {cicloMensal != null ? (
                  <span className="equipment-consult-v2-badge equipment-consult-v2-badge--muted">
                    Ciclo: {formatContador(cicloMensal)}
                  </span>
                ) : null}
              </div>
            </div>
            <button
              type="button"
              className="equipment-consult-v2-close"
              onClick={onClose}
              title="Fechar"
              aria-label="Fechar"
            >
              <X size={15} strokeWidth={2} />
            </button>
          </header>

          <div className="equipment-consult-v2-scroll">
            {loading && <div className="equipment-consult-v2-loading">Carregando informações…</div>}
            {!loading && error && (
              <p className="equipment-consult-v2-empty" style={{ color: '#ba1a1a' }}>
                {error}
              </p>
            )}

            {!loading && !error && (
              <>
                <div className="equipment-consult-v2-grid">
                  <div className="equipment-consult-v2-col equipment-consult-v2-col--left">
                    <section className="equipment-consult-v2-block">
                      <div className="equipment-consult-v2-block-head">
                        <h3 className="equipment-consult-v2-block-title">
                          <span className="equipment-consult-v2-block-icon" aria-hidden>
                            <Info size={12} strokeWidth={2} />
                          </span>
                          Informações do contrato
                        </h3>
                      </div>
                      <div className="equipment-consult-v2-block-body">
                        {!contract ? (
                          <p className="equipment-consult-v2-empty">Nenhum dado de contrato encontrado para esta série.</p>
                        ) : (
                          <>
                            {![
                              contract.CLIENTE,
                              contract.TB02111_CODIGO,
                              contract.TB02111_VENCCONTR,
                              contract.TB02112_CONTATO,
                              contract.TB02112_FONE,
                              contract.TB02112_EMAIL,
                              contract.TB02112_DATA,
                              contract.TB02176_NOME,
                              contract.ENDERECO,
                              contract.TB02112_LOCAL
                            ].some(hasText) && (
                              <p className="equipment-consult-v2-empty">Nenhum dado de contrato disponível.</p>
                            )}
                            <EcV2Field label="Cliente" valueClassName="equipment-consult-v2-field-value--cliente">
                              {contract.CLIENTE}
                            </EcV2Field>
                            {(hasText(contract.TB02111_CODIGO) || hasText(contract.TB02111_VENCCONTR)) && (
                              <div className="equipment-consult-v2-field-row">
                                {hasText(contract.TB02111_CODIGO) ? (
                                  <div className="equipment-consult-v2-field-cell">
                                    <span className="equipment-consult-v2-field-label">Contrato</span>
                                    <span className="equipment-consult-v2-mono">{contract.TB02111_CODIGO}</span>
                                  </div>
                                ) : null}
                                {hasText(contract.TB02111_VENCCONTR) ? (
                                  <div className="equipment-consult-v2-field-cell">
                                    <span className="equipment-consult-v2-field-label">Vencimento do contrato</span>
                                    <span className="equipment-consult-v2-field-value">
                                      <span className="equipment-consult-v2-vencc-wrap">
                                        <span>{contract.TB02111_VENCCONTR}</span>
                                        {isVencContratoAntesDeHoje(contract.TB02111_VENCCONTR) ? (
                                          <span
                                            className="equipment-consult-v2-vencc-alert-dot"
                                            title="Contrato vencido"
                                            role="img"
                                            aria-label="Contrato vencido"
                                          />
                                        ) : null}
                                      </span>
                                    </span>
                                  </div>
                                ) : null}
                              </div>
                            )}
                            {(hasText(contract.TB02112_CONTATO) || hasText(contract.TB02112_FONE)) && (
                              <div className="equipment-consult-v2-field-row">
                                {hasText(contract.TB02112_CONTATO) ? (
                                  <div className="equipment-consult-v2-field-cell">
                                    <span className="equipment-consult-v2-field-label">Responsável</span>
                                    <span className="equipment-consult-v2-field-value">{contract.TB02112_CONTATO}</span>
                                  </div>
                                ) : null}
                                {hasText(contract.TB02112_FONE) ? (
                                  <div className="equipment-consult-v2-field-cell">
                                    <span className="equipment-consult-v2-field-label">Telefone</span>
                                    <span className="equipment-consult-v2-field-value">{contract.TB02112_FONE}</span>
                                  </div>
                                ) : null}
                              </div>
                            )}
                            <EcV2Field label="Email">{contract.TB02112_EMAIL}</EcV2Field>
                            <EcV2Field label="Site">{contract.TB02176_NOME}</EcV2Field>
                            {hasText(contract.ENDERECO) && (
                              <div>
                                <span className="equipment-consult-v2-field-label">Endereço</span>
                                <div className="equipment-consult-v2-with-maps">
                                  <span className="equipment-consult-v2-field-value">{contract.ENDERECO}</span>
                                  <button
                                    type="button"
                                    className="order-detail-maps-btn"
                                    onClick={() => openGoogleMaps(contract.ENDERECO)}
                                    title="Abrir endereço no Google Maps"
                                  >
                                    <i className="bi bi-geo-alt" aria-hidden />
                                    Maps
                                  </button>
                                </div>
                              </div>
                            )}
                            <EcV2Field label="Local">{contract.TB02112_LOCAL}</EcV2Field>
                          </>
                        )}
                      </div>
                    </section>
                  </div>

                  <div className="equipment-consult-v2-col equipment-consult-v2-col--right">
                    <section className="equipment-consult-v2-block">
                      <div className="equipment-consult-v2-block-head">
                        <h3 className="equipment-consult-v2-block-title">
                          <span className="equipment-consult-v2-block-icon" aria-hidden>
                            <Wrench size={12} strokeWidth={2} />
                          </span>
                          Último atendimento
                        </h3>
                      </div>
                      <div className="equipment-consult-v2-block-body">
                        {!lastService ? (
                          <>
                            <p className="equipment-consult-v2-empty">Não há dados de atendimento encontrados.</p>
                            <EcHistoricoAtendimentoBlock
                              serie={serie}
                              historicoMounted={historicoMounted}
                              onToggle={toggleHistorico}
                            />
                          </>
                        ) : (
                          <>
                            {(hasText(lastService.TB02115_CODIGO)
                              || hasText(lastService.TB02115_PREVENTIVA)
                              || hasText(lastService.TB02115_DATA)) && (
                              <div className="equipment-consult-v2-field-row equipment-consult-v2-field-row--3">
                                {hasText(lastService.TB02115_CODIGO) ? (
                                  <div className="equipment-consult-v2-field-cell">
                                    <span className="equipment-consult-v2-field-label">OS</span>
                                    <span className="equipment-consult-v2-mono">{lastService.TB02115_CODIGO}</span>
                                  </div>
                                ) : null}
                                {hasText(lastService.TB02115_PREVENTIVA) ? (
                                  <div className="equipment-consult-v2-field-cell">
                                    <span className="equipment-consult-v2-field-label">Tipo</span>
                                    <span className="equipment-consult-v2-field-value">{lastService.TB02115_PREVENTIVA}</span>
                                  </div>
                                ) : null}
                                {hasText(lastService.TB02115_DATA) ? (
                                  <div className="equipment-consult-v2-field-cell">
                                    <span className="equipment-consult-v2-field-label">Data de abertura</span>
                                    <span className="equipment-consult-v2-field-value">{lastService.TB02115_DATA}</span>
                                  </div>
                                ) : null}
                              </div>
                            )}
                            <EcV2Field label="Técnico" valueClassName="equipment-consult-v2-field-value--tech">
                              {lastService.TB01024_NOME}
                            </EcV2Field>
                            <EcV2Field label="Data conclusão">{lastService.TB02122_DATAFIM}</EcV2Field>
                            <div>
                              <span className="equipment-consult-v2-field-label">Contadores</span>
                              <div className="equipment-consult-v2-contadores">
                                {CONTADOR_FIELDS.map(({ field, label }) => (
                                  <div key={field} className="equipment-consult-v2-contador-cell">
                                    <span className="equipment-consult-v2-field-label">{label}</span>
                                    <span className="equipment-consult-v2-field-value">
                                      {formatContador(lastService[field])}
                                    </span>
                                  </div>
                                ))}
                              </div>
                            </div>

                            <EcHistoricoAtendimentoBlock
                              serie={serie}
                              historicoMounted={historicoMounted}
                              onToggle={toggleHistorico}
                            />
                          </>
                        )}
                      </div>
                    </section>
                  </div>
                </div>

                <section className="equipment-consult-v2-block equipment-consult-v2-block--chart">
                  <div className="equipment-consult-chart-head">
                    <h3 className="equipment-consult-v2-block-title equipment-consult-chart-title">
                      <span className="equipment-consult-v2-block-icon" aria-hidden>
                        <BarChart3 size={12} strokeWidth={2} />
                      </span>
                      Últimos meses de produção
                    </h3>
                    <EquipmentConsultProductionPeriodPicker
                      currentPeriod={productionPeriod}
                      disabled={loading}
                      onApply={(startDate, endDate) => loadProduction({ startDate, endDate })}
                      onClear={() => loadProduction(getDefaultProductionPeriod())}
                    />
                  </div>
                {productionLoading && (
                  <p className="equipment-consult-v2-empty" style={{ padding: '8px 0' }}>
                    Carregando produção…
                  </p>
                )}
                {!productionLoading && productionError && (
                  <p className="equipment-consult-v2-empty" style={{ padding: '8px 0', color: '#ba1a1a' }}>
                    {productionError}
                  </p>
                )}
                {!productionLoading && !productionError && !chartData.length ? (
                  <p className="equipment-consult-v2-empty" style={{ padding: '12px 0' }}>
                    Não há dados de produção nos últimos meses.
                  </p>
                ) : null}
                {!productionLoading && !productionError && chartData.length > 0 ? (
                  <>
                    <div className="equipment-consult-chart-legend">
                      <span>
                        <span className="equipment-consult-chart-dot" style={{ background: '#7c4dff' }} />
                        P&amp;B
                      </span>
                      <span>
                        <span className="equipment-consult-chart-dot" style={{ background: '#06b6d4' }} />
                        Cor
                      </span>
                      <span>
                        <span className="equipment-consult-chart-dot" style={{ background: '#10b981' }} />
                        Digitalização
                      </span>
                      {cicloMensal != null ? (
                        <span>
                          <span className="equipment-consult-chart-dot equipment-consult-chart-dot--ciclo" />
                          Ciclo mensal
                        </span>
                      ) : null}
                    </div>
                    <div ref={chartWrapRef} className="equipment-consult-chart-canvas">
                      {chartWidth > 0 ? (
                        <AreaChart
                          width={chartWidth}
                          height={CHART_HEIGHT}
                          data={chartData}
                          margin={{ top: 14, right: 8, left: 4, bottom: 0 }}
                        >
                          <CartesianGrid strokeDasharray="3 3" stroke="#e2e8f0" vertical={false} />
                          <XAxis
                            dataKey="data"
                            tick={{ fontSize: 9, fill: '#64748b' }}
                            axisLine={{ stroke: '#e2e8f0' }}
                            tickLine={false}
                          />
                          <YAxis
                            tick={{ fontSize: 9, fill: '#64748b' }}
                            axisLine={false}
                            tickLine={false}
                            width={56}
                            domain={chartYMax ? [0, chartYMax] : ['auto', 'auto']}
                            tickFormatter={formatChartAxisTick}
                          />
                          {cicloMensal != null ? (
                            <ReferenceLine
                              y={cicloMensal}
                              stroke="#f59e0b"
                              strokeDasharray="5 4"
                              strokeWidth={1.5}
                              label={{
                                value: formatContador(cicloMensal),
                                position: 'insideTopRight',
                                fill: '#b45309',
                                fontSize: 10,
                                fontWeight: 600,
                              }}
                            />
                          ) : null}
                          <Tooltip
                            content={<ProductionChartTooltip />}
                            cursor={false}
                            isAnimationActive={false}
                            position={({ coordinate, viewBox }) => {
                              const { x, y } = getProductionTooltipLayout(coordinate, viewBox);
                              return { x, y };
                            }}
                            wrapperStyle={{
                              outline: 'none',
                              zIndex: 5,
                              pointerEvents: 'none',
                            }}
                          />
                          <Area
                            type="monotone"
                            dataKey="P&B"
                            stackId="a"
                            stroke="#7c4dff"
                            fill="#7c4dff"
                            fillOpacity={0.4}
                            isAnimationActive
                            animationDuration={900}
                            animationEasing="ease-out"
                          />
                          <Area
                            type="monotone"
                            dataKey="Cor"
                            stackId="a"
                            stroke="#06b6d4"
                            fill="#06b6d4"
                            fillOpacity={0.35}
                            isAnimationActive
                            animationDuration={900}
                            animationEasing="ease-out"
                          />
                          <Area
                            type="monotone"
                            dataKey="Digitalização"
                            stackId="a"
                            stroke="#10b981"
                            fill="#10b981"
                            fillOpacity={0.35}
                            isAnimationActive
                            animationDuration={900}
                            animationEasing="ease-out"
                          />
                        </AreaChart>
                      ) : null}
                    </div>
                  </>
                ) : null}
                </section>
              </>
            )}
          </div>
        </div>
      {historicoMounted ? (
        <OpenOsV2EquipmentHistoryOverlay
          isOpen={historicoOpen}
          onClose={closeHistorico}
          serie={serie}
          modelo={selection?.produto || contract?.TB01010_NOME || ''}
          patrimonio={selection?.patrimonio || contract?.TB02112_PAT || ''}
          instalado={contract?.TB02112_DATA || null}
          zIndex={10050}
        />
      ) : null}
      </div>
    </div>
  );

  return ReactDOM.createPortal(overlay, document.body);
};

export default EquipmentConsultDetailModal;
